/**
 * SSR/iframe/private-mode safe wrapper for window.localStorage.
 *
 * Accessing `window.localStorage` can throw a SecurityError in sandboxed
 * iframes, when third-party storage is blocked, or when the user has
 * disabled cookies/site data. setItem can also throw QuotaExceededError.
 *
 * This wrapper exposes the same Storage-like interface (getItem/setItem/
 * removeItem/clear/key/length) but silently degrades to a no-op when access
 * is denied so the booking flow keeps working in memory instead of crashing.
 *
 * Use this everywhere the booking app touches localStorage. Existing
 * try/catch sites are equivalent — but prefer this wrapper for consistency.
 */

const isStorageAvailable = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const testKey = "__safeStorage_test__";
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const available = isStorageAvailable();

export const safeLocalStorage: Storage = {
  get length(): number {
    if (!available) return 0;
    try {
      return window.localStorage.length;
    } catch {
      return 0;
    }
  },
  getItem(key: string): string | null {
    if (!available) return null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    if (!available) return;
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Quota exceeded or access denied — drop silently.
    }
  },
  removeItem(key: string): void {
    if (!available) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Access denied — drop silently.
    }
  },
  clear(): void {
    if (!available) return;
    try {
      window.localStorage.clear();
    } catch {
      // Access denied — drop silently.
    }
  },
  key(index: number): string | null {
    if (!available) return null;
    try {
      return window.localStorage.key(index);
    } catch {
      return null;
    }
  },
};

export const isLocalStorageAvailable = (): boolean => available;
